import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import type { ContactoItem } from '@/shared/api/controllers/contactos';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { USOS_CFDI } from './constants';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: ContactoItem | null;
};

export function ContactViewDialog({ open, onOpenChange, contact }: Props) {
  const { config: activeCountry } = useActiveCountry();
  const isMx = activeCountry.code === 'MX';
  if (!contact) return null;
  const f = contact.fields;
  const nombre = `${f.Nombre_C ?? ''} ${f.Apellido_C ?? ''}`.trim() || '—';
  const usoCfdiLabel = (() => {
    const code = f.UsoCFDI_C ?? '';
    if (!code) return '—';
    const found = USOS_CFDI.find((u) => u.code === code);
    return found ? `${found.code} — ${found.label}` : code;
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{nombre}</DialogTitle>
          <DialogDescription>Detalle del contacto</DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-6 text-sm">
          <ViewSection title="Datos del contacto">
            <Dl>
              <Dt>Mail</Dt>
              <Dd>{f.Mail_C || '—'}</Dd>
              <Dt>Celular</Dt>
              <Dd>{f.Celular_C || '—'}</Dd>
              <Dt>Medio</Dt>
              <Dd>{f.MedioDeLlegada_C || '—'}</Dd>
              {f.MedioDeLlegada_C === 'Referidos' && (
                <>
                  <Dt>Referido</Dt>
                  <Dd>{f.Referido_C || '—'}</Dd>
                </>
              )}
            </Dl>
          </ViewSection>

          <ViewSection title="Datos fiscales">
            <Dl>
              <Dt>{isMx ? 'RFC' : 'CUIT'}</Dt>
              <Dd>{f.CUIT_C || '—'}</Dd>
              <Dt>Razón Social</Dt>
              <Dd>{f.RazonSocial_C || '—'}</Dd>
              <Dt>{isMx ? 'Régimen Fiscal' : 'Condición IVA'}</Dt>
              <Dd>{f.CP_C || '—'}</Dd>
              {isMx && (
                <>
                  <Dt>Código Postal</Dt>
                  <Dd>{f.CodigoPostal_C || '—'}</Dd>
                  <Dt>Uso del CFDI</Dt>
                  <Dd>{usoCfdiLabel}</Dd>
                </>
              )}
              <Dt>{isMx ? 'Estado' : 'Provincia'}</Dt>
              <Dd>{f.Provincia_C || '—'}</Dd>
              <Dt>Ciudad</Dt>
              <Dd>{f.Ciudad_C || '—'}</Dd>
              <Dt>Dirección</Dt>
              <Dd>
                {[f.Direccion_C, f.Altura_C, f.Piso_C && `Piso ${f.Piso_C}`, f.Depto_C && `Depto ${f.Depto_C}`]
                  .filter(Boolean)
                  .join(' ') || '—'}
              </Dd>
            </Dl>
          </ViewSection>

          <ViewSection title="Datos de envío">
            <Dl>
              <Dt>Dirección completa</Dt>
              <Dd>{f.DireccionCompleta_C || '—'}</Dd>
              <Dt>Recibe</Dt>
              <Dd>{f.Receptor_C || '—'}</Dd>
              <Dt>Observaciones</Dt>
              <Dd>{f.Observaciones_C || 'Sin observaciones'}</Dd>
            </Dl>
          </ViewSection>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ViewSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      {children}
    </section>
  );
}

const Dl = ({ children }: { children: React.ReactNode }) => (
  <dl className="grid grid-cols-[10rem_1fr] gap-x-4 gap-y-2">{children}</dl>
);
const Dt = ({ children }: { children: React.ReactNode }) => (
  <dt className="text-muted-foreground">{children}</dt>
);
const Dd = ({ children }: { children: React.ReactNode }) => (
  <dd className="text-foreground">{children}</dd>
);
